import {useTheme} from "@/hooks/useTheme";
import clsxm from "@/lib/clsxm";
import {MARKDOWN_SYMBOLS_PATTERN, useComposedCampaignState} from "@/store/campaignForm";
import MDEditor, {
  commands,
  getBreaksNeededForEmptyLineAfter,
  getBreaksNeededForEmptyLineBefore,
  insertBeforeEachLine,
  selectWord,
  type AlterLineFunction,
  type ExecuteState,
  type TextAreaTextApi,
} from "@uiw/react-md-editor";
import {useRef, type FC, type ReactNode} from "react";

// This is a slightly modified version of the `makeList` utility function of the @uiw/react-md-editor library (v4.0.3).
// The customization fixes the issue of the first list symbol ('-' for unordered lists, '1.' for ordered lists) being automatically highlighted.
// This was problematic because typing would cause the list symbol and the entire list to disappear.
const makeList = (state: ExecuteState, api: TextAreaTextApi, insertBefore: string | AlterLineFunction) => {
  const newSelectionRange = selectWord({
    text: state.text,
    selection: state.selection,
    prefix: state.command.prefix!,
  });
  const state1 = api.setSelectionRange(newSelectionRange);

  const breaksBeforeCount = getBreaksNeededForEmptyLineBefore(state1.text, state1.selection.start);
  const breaksBefore = Array(breaksBeforeCount + 1).join("\n");

  const breaksAfterCount = getBreaksNeededForEmptyLineAfter(state1.text, state1.selection.end);
  const breaksAfter = Array(breaksAfterCount + 1).join("\n");

  const contentBeforeSelection = state.text.substring(0, state.selection.start);
  const isTextOrNonEmptyList = /^(?:[^\S\n]*\S[^\n]*\n+)*[\s]*$/.test(contentBeforeSelection);

  const {modifiedText, insertionLength} = insertBeforeEachLine(state1.selectedText, insertBefore);
  if (isTextOrNonEmptyList) {
    api.replaceSelection(`${breaksBefore}${modifiedText}${breaksAfter}`);
  } else {
    if (insertionLength < 0) {
      let selectionStart = state1.selection.start;
      let selectionEnd = state1.selection.end;
      if (state1.selection.start > 0 && state.text.slice(state1.selection.start - 1, state1.selection.start) === "\n") {
        selectionStart -= 1;
      }
      if (
        state1.selection.end < state.text.length - 1 &&
        state.text.slice(state1.selection.end, state1.selection.end + 1) === "\n"
      ) {
        selectionEnd += 1;
      }

      api.setSelectionRange({start: selectionStart, end: selectionEnd});
      api.replaceSelection(`${modifiedText}`);
      api.setSelectionRange({
        start: selectionStart,
        end: selectionStart + modifiedText.length,
      });
    } else {
      api.replaceSelection(`${breaksBefore}${modifiedText}${breaksAfter}`);
      const selectionStart = state1.selection.start + breaksBeforeCount;
      const selectionEnd = selectionStart + modifiedText.length;

      api.setSelectionRange({start: selectionStart, end: selectionEnd});
    }
  }
};

const MarkdownEditor: FC<{
  bottomContent?: ReactNode;
  height?: number;
  value: string;
  onBlur?: () => void;
  onChange: (value?: string) => void;
  onFocus?: () => void;
  onKeyDown?: () => void;
  maxChars?: number;
  maxCharsClassName?: string;
  maxCharsCorrectClassName?: string;
  placeholder?: string;
  className?: string;
  visibleDragbar?: boolean;
  autoSize?: boolean;
}> = ({
  bottomContent,
  height,
  value,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  maxChars,
  maxCharsClassName,
  maxCharsCorrectClassName,
  placeholder,
  className,
  visibleDragbar = true,
  autoSize = false,
}) => {
  const mdEditorRef = useRef<{textareaWarp: HTMLDivElement}>(null);
  const skipMaxlengthCheck = useComposedCampaignState(state => state.misc.skipCharacterLimits);
  const sanitizedLength = value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length;
  const {resolvedTheme} = useTheme();

  return (
    <div data-color-mode={resolvedTheme}>
      <MDEditor
        ref={mdEditorRef}
        height={autoSize ? undefined : height}
        value={value}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onChange={e => {
          let inputValue = e;
          const lines = inputValue?.split("\n");
          const isDeleting = inputValue?.length === value.length - 1;

          if (lines && (lines.length ?? 0) > 1) {
            const lastLine = lines[lines.length - 1];
            const secondToLastLine = lines[lines.length - 2];

            // Automatically prepend unordered list symbol to newer list items
            if (secondToLastLine.startsWith("- ") && !lastLine.trim().length && !isDeleting) {
              lines[lines.length - 1] = `- ${lastLine}`;
              inputValue = lines.join("\n");
            }

            // Automatically prepend ordered list symbol to newer list items
            if (/^\d+\./.test(secondToLastLine) && !lastLine.trim().length && !isDeleting) {
              const previousListItemIndex = +secondToLastLine.split(".")[0];
              lines[lines.length - 1] = `${previousListItemIndex + 1}. ${lastLine}`;
              inputValue = lines.join("\n");
            }
          }

          onChange(inputValue);
        }}
        onFocus={onFocus}
        commands={[
          commands.bold,
          commands.italic,
          commands.divider,
          commands.link,
          commands.divider,
          {
            ...commands.unorderedListCommand,
            execute: (state: ExecuteState, api: TextAreaTextApi) => {
              makeList(state, api, "- ");
            },
          },
          {
            ...commands.orderedListCommand,
            execute: (state: ExecuteState, api: TextAreaTextApi) => {
              makeList(state, api, (_, index) => `${index + 1}. `);
            },
          },
        ]}
        extraCommands={[]}
        preview="edit"
        textareaProps={{placeholder}}
        className={clsxm("bg-white", autoSize && "h-auto", className)}
        visibleDragbar={visibleDragbar}
      />
      {bottomContent && <div className="relative">{bottomContent}</div>}
      {maxChars && (
        <div className="relative">
          <span
            className={clsxm(
              "absolute -top-6 right-5",
              sanitizedLength <= maxChars || skipMaxlengthCheck
                ? clsxm("text-good-grey-400", maxCharsCorrectClassName)
                : "text-red-500",
              maxCharsClassName,
            )}>
            {sanitizedLength}/{maxChars}
          </span>
        </div>
      )}
    </div>
  );
};

export default MarkdownEditor;
